import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Card from '../ui/Card';
import Button from '../ui/Button';
import PersonFilters from './filters/PersonFilters';
import CompanyFilters from './filters/CompanyFilters';
import LocationFilters from './filters/LocationFilters';
import SubscriptionStatus from '../user/SubscriptionStatus';
import useSubscription from '../../hooks/useSubscription';
import { useSettings } from '../../hooks/useSettings';
import { resolveContentLayout } from '../../theme/presets';
import { Download, Info } from 'lucide-react';

const LeadFinderPage = () => {
  const { settings: appSettings } = useSettings();
  // Focused layout drops the subscription card; those figures live in the
  // credits pill in UserLayout instead.
  const isFocused = resolveContentLayout(appSettings?.content_layout) === 'focused';
  // Handle login-as token on mount (same logic as Dashboard)
  // This ensures other axios calls on this page use the correct token
  useEffect(() => {
    const sessionLoginAsToken = sessionStorage.getItem('loginAsToken');
    if (sessionLoginAsToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${sessionLoginAsToken}`;
    }
  }, []);

  // Modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // List name for job identification
  const [listName, setListName] = useState('');

  // Filter state (snake_case to match backend)
  const [filters, setFilters] = useState({
    person_title: [],
    seniority: [],
    functional: [],
    company_employee_size: [],
    industry: [],
    industry_keywords: [],
    revenue: [],
    business_model: [],
    company_domain: [],
    person_country: [],
    person_state: [],
    company_country: [],
    company_state: []
  });

  const [leadCount, setLeadCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Job history state
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState(null);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [hoveredJobId, setHoveredJobId] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');

  // Subscription state - using centralized hook (now uses axios.create for isolation)
  const { subscription, subscriptionLoading } = useSubscription();

  // Settings state (for timezone)
  const [settings, setSettings] = useState(null);

  // Fetch jobs on mount and after job submission
  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);
      setJobsError(null);

      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/lead-finder/jobs?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to fetch jobs');
      }

      setJobs(data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setJobsError(err.message);
    } finally {
      setLoadingJobs(false);
    }
  };

  // Download handler
  const handleDownload = async (jobId, listName) => {
    setDownloadingJobId(jobId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/lead-finder/jobs/${jobId}/download`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.status === 202) {
        // File is being prepared
        alert('Your file is being prepared. Please try again in a few moments.');
        return;
      }

      if (!response.ok) {
        let detail = '';
        try {
          const body = await response.json();
          if (body && body.detail) detail = `: ${body.detail}`;
        } catch (_) {
          // Not JSON; the status is still worth surfacing.
        }
        throw new Error(`Download failed (error ${response.status})${detail}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${listName || 'leads'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please try again.');
    } finally {
      setDownloadingJobId(null);
    }
  };

  // Clone job handler - prefills form with job's filters
  const handleCloneJob = (job) => {
    // Reset any previous state
    setError(null);
    setLeadCount(null);

    // Prefill list name with "Copy of {original name}"
    setListName(`Copy of ${job.list_name}`);

    // Prefill filters from the job
    // Note: Filters are stored in camelCase (Apify format) but we need snake_case for frontend
    if (job.filters) {
      const newFilters = {
        person_title: job.filters.personTitle || job.filters.person_title || [],
        seniority: job.filters.seniority || [],
        functional: job.filters.functional || [],
        company_employee_size: job.filters.companyEmployeeSize || job.filters.company_employee_size || [],
        industry: job.filters.industry || [],
        industry_keywords: job.filters.industryKeywords || job.filters.industry_keywords || [],
        revenue: job.filters.revenue || [],
        business_model: job.filters.businessModel || job.filters.business_model || [],
        company_domain: job.filters.companyDomain || job.filters.company_domain || [],
        person_country: job.filters.personCountry || job.filters.person_country || [],
        person_state: job.filters.personState || job.filters.person_state || [],
        company_country: job.filters.companyCountry || job.filters.company_country || [],
        company_state: job.filters.companyState || job.filters.company_state || []
      };
      setFilters(newFilters);
    }

    // Open the form modal
    setShowFormModal(true);
  };

  // Fetch settings and jobs on mount (subscription handled by useSubscription hook)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
        if (!token) return;

        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await axios.get(`${apiUrl}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 10000
        });
        setSettings(response.data);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      }
    };

    fetchSettings();
    fetchJobs(); // Load jobs on mount
  }, []);

  // Client-side sorting and filtering
  const sortedAndFilteredJobs = useMemo(() => {
    let filtered = jobs;

    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = jobs.filter(job => (job.list_name || '').toLowerCase().includes(q));
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle date sorting
      if (sortBy === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      // Handle numeric sorting
      else if (sortBy === 'estimated_leads' || sortBy === 'result_count') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }
      // Handle string sorting
      else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [jobs, searchQuery, sortBy, sortDir]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleCheckCount = async () => {
    setLoading(true);
    setLeadCount(null);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/lead-finder/count`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(getFiltersForCount())
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Failed to fetch lead count';

        setError({
          message: errorMessage,
          status: response.status
        });
        return;
      }

      const data = await response.json();
      setLeadCount(data.total_leads);
    } catch (error) {
      console.error('Error fetching lead count:', error);
      setError({
        message: error.message || 'An unexpected error occurred. Please try again.',
        status: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setListName('');
    setFilters({
      person_title: [],
      seniority: [],
      functional: [],
      company_employee_size: [],
      industry: [],
      industry_keywords: [],
      revenue: [],
      business_model: [],
      company_domain: [],
      person_country: [],
      person_state: [],
      company_country: [],
      company_state: []
    });
    setLeadCount(null);
    setError(null);
  };

  const handleOpenFormModal = () => {
    setShowFormModal(true);
    setSubmitSuccess(false);
    setError(null);
    setLeadCount(null);
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    handleResetFilters();
  };

  const handleProceedToConfirm = () => {
    setShowFormModal(false);
    setShowConfirmModal(true);
  };

  const handleSubmitJob = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/lead-finder/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          list_name: listName.trim(),
          filters: getCleanFilters()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit job');
      }

      setSubmitSuccess(true);
      console.log('Job submitted successfully:', data);

      // Reset form and close modals after 2 seconds
      setTimeout(() => {
        setShowConfirmModal(false);
        handleResetFilters();
        fetchJobs(); // Refresh job list
      }, 2000);

    } catch (err) {
      console.error('Failed to submit job:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Check if any filter is set
  const hasFilters = () => {
    return Object.entries(filters).some(([key, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && value !== '';
    });
  };

  // Get filters for count endpoint (no email preferences)
  const getFiltersForCount = () => {
    const cleaned = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (value === '') return;
      if (Array.isArray(value) && value.length === 0) return;
      cleaned[key] = value;
    });
    return cleaned;
  };

  // Get clean JSON for scraping (includes email preferences hardcoded to true)
  const getCleanFilters = () => {
    const cleaned = getFiltersForCount();
    cleaned.include_emails = true;
    cleaned.skip_leads_without_emails = true;
    return cleaned;
  };

  return (
    <div style={{
      padding: '24px',
      maxWidth: 1400,
      margin: '0 auto',
      minHeight: 'calc(100vh - 100px)'
    }}>
      {/* Top Row: CTA Card and Subscription Card side by side */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: 24,
        marginBottom: 24
      }}>
        {/* Call to Action Card */}
        <Card style={{
          background: 'linear-gradient(135deg, #faf5ff 0%, white 100%)',
          border: '1px solid #e9d5ff'
        }}>
          <Card.Content>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div>
                <h2 style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: 'var(--color-info-fg)',
                  margin: 0,
                  marginBottom: 8
                }}>
                  Ready to find qualified leads?
                </h2>
                <p style={{
                  color: 'var(--color-text-muted)',
                  margin: 0,
                  fontSize: 14
                }}>
                  Configure filters and discover prospects matching your ideal customer profile
                </p>
              </div>
              <Button onClick={handleOpenFormModal}>
                🔍 Find New Leads
              </Button>
            </div>
          </Card.Content>
        </Card>

        {/* Subscription status - hidden in the focused layout */}
        {!isFocused && (
          <SubscriptionStatus
            subscription={subscription}
            subscriptionLoading={subscriptionLoading}
          />
        )}
      </div>

      {/* Lead Finder Jobs Table */}
      <Card>
        <Card.Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Card.Title>Lead Finder Jobs</Card.Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                placeholder="Search by list name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '8px 10px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  fontSize: 14
                }}
              />
              <Button
                variant="secondary"
                onClick={fetchJobs}
                disabled={loadingJobs}
              >
                Refresh
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Content style={{ padding: 0 }}>
          {loadingJobs ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '12px' }}>⏳</div>
              <div style={{ color: 'var(--color-text-muted)' }}>Loading job history...</div>
            </div>
          ) : jobsError ? (
            <div style={{
              background: 'var(--color-danger-bg)',
              border: '1px solid var(--color-danger-border)',
              borderRadius: '8px',
              padding: '20px',
              margin: '16px',
              color: 'var(--color-danger-fg)'
            }}>
              ❌ Failed to load jobs: {jobsError}
            </div>
          ) : jobs.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              border: '2px dashed var(--color-border)',
              margin: '16px',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--color-text-primary)'
              }}>
                No jobs yet
              </h3>
              <p style={{
                margin: '0 0 16px 0',
                fontSize: '14px',
                color: 'var(--color-text-muted)'
              }}>
                Click "Find New Leads" above to create your first Lead Finder job
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0
              }}>
                <thead>
                  <tr>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const nextDir = sortBy === 'created_at' && sortDir === 'desc' ? 'asc' : 'desc';
                      setSortBy('created_at');
                      setSortDir(nextDir);
                    }}>
                      Date {sortBy === 'created_at' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const nextDir = sortBy === 'list_name' && sortDir === 'desc' ? 'asc' : 'desc';
                      setSortBy('list_name');
                      setSortDir(nextDir);
                    }}>
                      List Name {sortBy === 'list_name' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const nextDir = sortBy === 'status' && sortDir === 'desc' ? 'asc' : 'desc';
                      setSortBy('status');
                      setSortDir(nextDir);
                    }}>
                      Status {sortBy === 'status' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const nextDir = sortBy === 'estimated_leads' && sortDir === 'desc' ? 'asc' : 'desc';
                      setSortBy('estimated_leads');
                      setSortDir(nextDir);
                    }}>
                      Requested {sortBy === 'estimated_leads' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const nextDir = sortBy === 'result_count' && sortDir === 'desc' ? 'asc' : 'desc';
                      setSortBy('result_count');
                      setSortDir(nextDir);
                    }}>
                      Extracted {sortBy === 'result_count' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)'
                    }}>
                      Export Data
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAndFilteredJobs.map((job) => (
                    <tr key={job.id} style={{ borderBottom: '1px solid var(--color-bg-inset)' }}>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: 'var(--color-text-secondary)'
                      }}>
                        {new Date((job.created_at || '').includes('Z') ? job.created_at : job.created_at + 'Z').toLocaleString('en-GB', {
                          timeZone: (settings?.timezone || 'Asia/Kolkata'),
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: true
                        })}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: 'var(--color-text-primary)',
                        fontWeight: 600
                      }}>
                        <button
                          onClick={() => handleCloneJob(job)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-info-hover)',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '13px',
                            fontWeight: 600,
                            padding: 0,
                            textAlign: 'left'
                          }}
                          onMouseEnter={(e) => e.target.style.color = 'var(--color-info-hover)'}
                          onMouseLeave={(e) => e.target.style.color = 'var(--color-info-hover)'}
                          title="Click to view parameters and create similar job"
                        >
                          {job.list_name}
                        </button>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative', zIndex: hoveredJobId === job.id ? 1001 : 'auto' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: 12,
                            background: job.status === 'completed' ? 'var(--color-success-bg)' : job.status === 'failed' ? 'var(--color-danger-bg)' : 'var(--color-warning-bg)',
                            color: job.status === 'completed' ? 'var(--color-success-fg)' : job.status === 'failed' ? 'var(--color-danger-hover)' : 'var(--color-warning-hover)',
                            border: '1px solid var(--color-border)'
                          }}>
                            {job.status}
                          </span>
                          {job.status === 'failed' && job.error_message && (
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'help',
                                color: 'var(--color-danger-hover)',
                                transition: 'transform 0.2s',
                                position: 'relative'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.1)';
                                setHoveredJobId(job.id);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                setHoveredJobId(null);
                              }}
                            >
                              <Info size={16} strokeWidth={2} />

                              {/* Custom Tooltip */}
                              {hoveredJobId === job.id && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    marginTop: '8px',
                                    backgroundColor: 'var(--color-text-primary)',
                                    color: '#ffffff',
                                    padding: '10px 14px',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    lineHeight: '1.5',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
                                    zIndex: 9999,
                                    minWidth: '200px',
                                    maxWidth: '350px',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    pointerEvents: 'none'
                                  }}
                                >
                                  <div style={{
                                    fontWeight: '600',
                                    marginBottom: '6px',
                                    color: '#fca5a5',
                                    fontSize: '12px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Failure Reason
                                  </div>
                                  {job.error_message}
                                  {/* Tooltip arrow */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '100%',
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      width: 0,
                                      height: 0,
                                      borderLeft: '6px solid transparent',
                                      borderRight: '6px solid transparent',
                                      borderBottom: '6px solid var(--color-text-primary)'
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 13
                      }}>
                        {(job.estimated_leads || 0).toLocaleString()}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 13
                      }}>
                        {(job.result_count || 0).toLocaleString()}
                      </td>
                      <td style={{
                        padding: '10px 12px'
                      }}>
                        {job.status === 'completed' ? (
                          <Button
                            size="sm"
                            onClick={() => handleDownload(job.id, job.list_name)}
                            icon={<Download size={14} />}
                            loading={downloadingJobId === job.id}
                            disabled={downloadingJobId === job.id}
                          >
                            {downloadingJobId === job.id ? 'Preparing...' : 'Excel'}
                          </Button>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--color-text-disabled)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Content>
      </Card>

      {/* Lead Finder Form Modal */}
      {showFormModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '40px 20px',
          overflowY: 'auto'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: 'calc(100vh - 80px)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  margin: 0
                }}>
                  🔍 Configure Lead Finder
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--color-text-muted)',
                  margin: '4px 0 0 0'
                }}>
                  Set up filters to find your ideal prospects
                </p>
              </div>
              <button
                onClick={handleCloseFormModal}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                  padding: '8px',
                  fontSize: '24px',
                  lineHeight: 1,
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-inset)'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                ×
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px'
            }}>
              {/* List Name */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '8px'
                }}>
                  List Name <span style={{ color: 'var(--color-danger)' }}>*</span>
                </label>
                <input
                  type="text"
                  value={listName}
                  onChange={(e) => setListName(e.target.value)}
                  placeholder="e.g., Q1 2024 Enterprise Prospects"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--color-border-strong)',
                    borderRadius: '6px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--color-info)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--color-border-strong)'}
                />
                <p style={{
                  fontSize: '12px',
                  color: 'var(--color-text-muted)',
                  marginTop: '6px',
                  marginBottom: 0
                }}>
                  Give this lead list a unique name to identify it in your job history
                </p>
              </div>

              {/* Person Filters */}
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: 'var(--color-bg-subtle)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 16px 0'
                }}>
                  👤 Person Filters
                </h3>
                <PersonFilters filters={filters} onFilterChange={handleFilterChange} />
              </div>

              {/* Company Filters */}
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: 'var(--color-bg-subtle)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 16px 0'
                }}>
                  🏢 Company Filters
                </h3>
                <CompanyFilters filters={filters} onFilterChange={handleFilterChange} />
              </div>

              {/* Location Filters */}
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: 'var(--color-bg-subtle)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 16px 0'
                }}>
                  🌍 Location Filters
                </h3>
                <LocationFilters filters={filters} onFilterChange={handleFilterChange} />
              </div>

              {/* Error Display */}
              {error && (
                <div style={{
                  background: error.status === 404 ? 'var(--color-warning-bg-strong)' : 'var(--color-danger-bg-strong)',
                  border: `1px solid ${error.status === 404 ? 'var(--color-warning-border)' : 'var(--color-danger-border)'}`,
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>
                    {error.status === 404 ? '🔍' : '⚠️'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: error.status === 404 ? 'var(--color-warning-fg)' : 'var(--color-danger-fg)',
                      marginBottom: '4px'
                    }}>
                      {error.status === 404 ? 'No Results Found' : 'Error'}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: error.status === 404 ? 'var(--color-warning-fg)' : 'var(--color-danger-fg)'
                    }}>
                      {error.message}
                    </div>
                  </div>
                  <button
                    onClick={() => setError(null)}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: error.status === 404 ? 'var(--color-warning-fg)' : 'var(--color-danger-fg)',
                      cursor: 'pointer',
                      padding: '4px',
                      fontSize: '18px',
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Count Display */}
              {leadCount !== null && !error && (
                <div style={{
                  background: 'var(--gradient-success)',
                  border: '2px solid var(--color-success-hover)',
                  borderRadius: '8px',
                  padding: '24px',
                  marginBottom: '20px',
                  boxShadow: '0 4px 12px rgba(5, 150, 105, 0.2)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}>
                    <span style={{ fontSize: '36px' }}>✅</span>
                    <div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: 'white',
                        marginBottom: '4px'
                      }}>
                        Lead Count Found
                      </div>
                      <div style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        color: 'white',
                        lineHeight: 1
                      }}>
                        {leadCount.toLocaleString()} leads
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer - Sticky */}
            <div style={{
              padding: '20px 24px',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-bg-subtle)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <Button
                variant="secondary"
                onClick={handleResetFilters}
                disabled={!hasFilters()}
                style={{
                  color: 'var(--color-danger-hover)',
                  borderColor: '#fca5a5',
                  opacity: !hasFilters() ? 0.5 : 1
                }}
              >
                🔄 Reset Filters
              </Button>
              <Button
                variant="secondary"
                onClick={handleCheckCount}
                disabled={!hasFilters() || loading}
              >
                {loading ? '⏳ Checking...' : '🔢 Check Count'}
              </Button>
              {leadCount !== null && !error && (
                <Button
                  variant="primary"
                  onClick={handleProceedToConfirm}
                  disabled={!hasFilters() || !listName.trim()}
                  title={!listName.trim() ? 'Please enter a list name' : ''}
                >
                  Continue →
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Job Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%'
          }}>
            {!submitSuccess ? (
              <>
                <h3 style={{
                  marginTop: 0,
                  marginBottom: '16px',
                  fontSize: '20px',
                  fontWeight: '600'
                }}>
                  Confirm Lead Finder Job
                </h3>

                <div style={{
                  background: 'var(--color-bg-subtle)',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                      List Name
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-text-primary)' }}>
                      {listName}
                    </div>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                      Estimated Leads
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-text-primary)' }}>
                      {leadCount?.toLocaleString()} leads
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                      Credits Required
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-text-primary)' }}>
                      {leadCount?.toLocaleString()} credits
                    </div>
                  </div>
                </div>

                {/* Credit adjustment notice */}
                <div style={{
                  background: 'var(--color-info-bg)',
                  border: '1px solid var(--color-info-border)',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: 'var(--color-info-fg)',
                  lineHeight: '1.5'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    ℹ️ Credit Adjustment Policy
                  </div>
                  This is an estimated lead count. If the actual extracted leads differ, we will automatically adjust your credits accordingly: refunding if fewer leads are found, or deducting additional credits if more leads are extracted.
                </div>

                {error && (
                  <div style={{
                    background: 'var(--color-danger-bg)',
                    border: '1px solid var(--color-danger-border)',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '16px',
                    color: 'var(--color-danger-fg)',
                    fontSize: '14px'
                  }}>
                    ❌ {error}
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowConfirmModal(false);
                      setShowFormModal(true);
                    }}
                    disabled={submitting}
                  >
                    ← Back
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSubmitJob}
                    disabled={submitting}
                  >
                    {submitting ? '⏳ Submitting...' : '🚀 Submit Job'}
                  </Button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                <h3 style={{
                  marginTop: 0,
                  marginBottom: '8px',
                  color: 'var(--color-success-hover)'
                }}>
                  Job Submitted Successfully!
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--color-text-muted)',
                  margin: 0
                }}>
                  Your leads are being collected. Check your job history for updates.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadFinderPage;
